import type { ShareFormat } from '../types/share'

/**
 * Valid file extensions and their corresponding format
 */
const EXTENSION_TO_FORMAT: Record<string, ShareFormat> = {
  '.md': 'md',
  '.markdown': 'md',
  '.mdc': 'mdc',
}

/**
 * Reverse mapping: format to preferred extension
 */
export const FORMAT_TO_EXTENSION: Record<ShareFormat, string> = {
  'md': '.md',
  'mdc': '.mdc'
}

/**
 * Get all valid extensions as a regex pattern
 */
const VALID_EXTENSIONS_REGEX = /\.(md|markdown|mdc)$/i

/**
 * Extract format from a filename based on its extension
 * Returns 'md' as default if no valid extension found
 */
export function detectFormatFromFilename(filename: string): ShareFormat {
  const lowerFilename = filename.toLowerCase()

  for (const [ext, format] of Object.entries(EXTENSION_TO_FORMAT)) {
    if (lowerFilename.endsWith(ext)) {
      return format
    }
  }

  return 'md' // Default to markdown
}

/**
 * Get the file extension for a given format
 */
export function getExtensionForFormat(format: ShareFormat): string {
  return FORMAT_TO_EXTENSION[format]
}

/**
 * Check if a filename has a valid extension
 */
export function hasValidExtension(filename: string): boolean {
  return VALID_EXTENSIONS_REGEX.test(filename)
}

/**
 * Remove the extension from a filename if it's a valid extension
 */
export function removeExtension(filename: string): string {
  return filename.replace(VALID_EXTENSIONS_REGEX, '')
}

/**
 * Extract extension from filename (lowercase, with dot)
 * Returns null if no valid extension found
 */
export function getExtension(filename: string): string | null {
  const match = filename.match(VALID_EXTENSIONS_REGEX)
  if (match) {
    const ext = match[0].toLowerCase()
    // Normalize .markdown to .md
    return ext === '.markdown' ? '.md' : ext
  }
  return null
}

/**
 * Ensure filename has the correct extension for the format
 * If filename already has a valid extension, preserve the base name and add correct extension
 * If no extension, add the format's extension
 */
export function ensureExtension(filename: string, format: ShareFormat): string {
  const baseName = removeExtension(filename)
  return baseName + FORMAT_TO_EXTENSION[format]
}

/**
 * Parse a filename into base name and format
 */
export function parseFilename(filename: string): { baseName: string; format: ShareFormat } {
  const format = detectFormatFromFilename(filename)
  const baseName = removeExtension(filename)
  return { baseName: baseName || 'untitled', format }
}

/**
 * Check if a file has a valid text-based extension for import
 */
export function isValidTextFile(filename: string): boolean {
  return VALID_EXTENSIONS_REGEX.test(filename.toLowerCase())
}

/**
 * Get the display extension for the editor header
 * Shows the actual extension based on filename or format
 */
export function getDisplayExtension(filename: string, format: ShareFormat): string {
  const ext = getExtension(filename)
  if (ext) {
    return ext
  }
  return FORMAT_TO_EXTENSION[format]
}

/**
 * Check if a filename has any extension (valid or not)
 */
export function hasAnyExtension(filename: string): boolean {
  return /\.[a-zA-Z0-9]+$/.test(filename)
}

/**
 * Validate filename and return result
 * Returns { valid: true, format } if filename is acceptable
 * Returns { valid: false, error } if filename has invalid extension
 */
export function validateFilename(filename: string):
  | { valid: true; format: ShareFormat }
  | { valid: false; error: string } {
  // Check if has any extension
  if (hasAnyExtension(filename)) {
    // Must be a valid extension
    if (hasValidExtension(filename)) {
      return { valid: true, format: detectFormatFromFilename(filename) }
    }
    return { valid: false, error: 'Only .md and .mdc extensions are allowed' }
  }
  // No extension is fine
  return { valid: true, format: 'md' }
}

/**
 * Generate a copy filename by appending or incrementing "(copy N)" suffix.
 * "readme" → "readme (copy)"
 * "readme (copy)" → "readme (copy 2)"
 * "readme (copy 3)" → "readme (copy 4)"
 * "readme.md" → "readme (copy)"
 */
export function generateCopyFilename(originalFilename?: string): string {
  if (!originalFilename) return 'untitled'

  const baseName = removeExtension(originalFilename) || originalFilename.replace(/\.md$/, '')
  const copyMatch = baseName.match(/^(.+?)\s*\(copy(?:\s+(\d+))?\)$/i)
  if (copyMatch) {
    const name = copyMatch[1]
    const num = copyMatch[2] ? parseInt(copyMatch[2], 10) + 1 : 2
    return `${name} (copy ${num})`
  }

  return `${baseName} (copy)`
}

/**
 * Parse share ID from a URL parameter, stripping known file extensions.
 * e.g. "abc123.md" → "abc123", "abc123.markdown" → "abc123", "abc123" → "abc123"
 */
export function parseShareIdParam(idParam: string): string {
  const extensionMatch = idParam.match(/^(.+?)(\.(?:md|markdown|mdc))$/i)
  return extensionMatch?.[1] ?? idParam
}
